import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { Shield, Plus } from 'lucide-react';
import PageHeading from '../../components/PageHeading';

function Admins() {
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  if (user?.role === 'WORKER') {
    return <Navigate to="/" replace />;
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admins', { search, statusFilter }],
    queryFn: async () => {
      const response = await api.get('/admins', {
        params: {
          search: search || undefined,
          status: statusFilter,
        },
      });
      const body = response.data || {};
      return {
        data: Array.isArray(body.data) ? body.data : [],
        pagination: body.pagination || { total: 0, page: 1, limit: 50, totalPages: 0 },
      };
    },
    enabled: user?.role !== 'WORKER',
  });

  const admins = data?.data || [];

  return (
    <div>
      <PageHeading
        title="Admins"
        infoTitle="Admins"
        infoChildren={
          <p>
            Office / manager accounts for the web admin. Each person should have their own login so audit logs show
            who changed schedules, jobs, and buildings.
          </p>
        }
        actions={
          (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
            <Link
              to="/admins/new"
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Admin
            </Link>
          )
        }
      />

      <div className="flex flex-col gap-4 mb-6">
        <div className="w-full md:w-2/3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by username, name, or email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div className="flex gap-2">
          {['active', 'all', 'inactive'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 text-sm rounded-lg border ${
                statusFilter === key
                  ? 'bg-primary-100 text-primary-800 border-primary-300'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {key === 'active' ? 'Active' : key === 'all' ? 'All' : 'Inactive'}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="text-center py-12 text-gray-500">Loading...</div>}
      {error && (
        <div className="text-center py-12 text-red-600">
          Failed to load admins: {error.message}
        </div>
      )}

      {!isLoading && !error && admins.length === 0 && (
        <div className="text-center py-16 bg-card rounded-lg shadow">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">No admin accounts found.</p>
          {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && (
            <Link
              to="/admins/new"
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Admin
            </Link>
          )}
        </div>
      )}

      {!isLoading && admins.length > 0 && (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/admins/${admin.id}`} className="text-primary-700 font-medium hover:underline">
                      {admin.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{admin.username}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{admin.email || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{admin.role}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        admin.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Admins;
